import { 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  signOut, 
  onAuthStateChanged, 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { firebaseObject } from '../config/firebase';
import { apiService } from './ApiService';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  picture: string | null;
  role: string | null; // Backend role field
  id?: number; // Backend database ID
  name?: string; // Backend name field (might differ from displayName)
  is_active?: boolean; // Whether the user account is active
  organization_id?: number | null; // Organization ID if user belongs to an org
  organization_name?: string | null; // Name of the current (working) organization
  // Global admins only: the org they're an actual member of, and whether they
  // have switched into a different one (drives the app-wide warning).
  home_organization_id?: number | null;
  home_organization_name?: string | null;
  is_outside_home_org?: boolean;
  language?: string; // Effective language from org or user settings
  currency?: string; // Effective currency from org or user settings
  number_format?: string; // Number format: 'comma' (1,000.00) or 'dot' (1.000,00)
  vat_rate?: number | null; // VAT/Tax rate percentage from org settings
  // Cart availability/pricing display (org settings)
  inventory_source_priority?: string; // 'dms_first' | 'importer_first' | 'dms_only' | 'importer_only'
  inventory_show_alternative?: boolean; // include the DMS "alternative" line-type
  inventory_show_importer_qty?: boolean; // importer stock as a unit count, not just In/Out
  inventory_price_priority?: string; // 'original_first' | 'alternative_first' | 'lowest_available_first'
  delivery_default_days?: number; // fallback delivery estimate (days) when the delivery_time feature is on
  delivery_zones?: { home_city_label?: string; home_city_days?: number; outside_days?: number; international_days?: number }; // predefined delivery options for the cart dropdown
  inventory_location_priority?: string; // 'list_order' | 'home_then_lowest' | 'home_then_first' | 'lowest' — default warehouse policy
  warehouses?: { code: string; name: string; abbrev: string }[]; // org-defined warehouses
  home_warehouse?: string | null; // legacy: first home warehouse code
  home_city?: string | null; // this member's home city (matches warehouses[].name)
  home_warehouses?: string[]; // every warehouse code in the home city (server-resolved)
  // ROI Calculator org-level settings
  roi_calculator_enabled?: boolean;
  roi_b2b_enabled?: boolean;
  roi_b2c_enabled?: boolean;
  roi_monthly_quotes?: number | null;
  roi_fte_multiplier?: number | null;
  roi_currency?: string | null;
  roi_fte_cost?: number | null;
  roi_cost_per_quote?: number | null;
  // B2C ROI Calculator defaults
  roi_b2c_quotes?: number | null;
  roi_b2c_cr?: number | null;
  roi_b2c_aov?: number | null;
  roi_b2c_gm?: number | null;
  roi_b2c_cpq?: number | null;
  roi_b2c_pct?: number | null;
  roi_b2c_pricing_mode?: string | null;
}

export interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUser: AuthUser | null;
  originalAdmin: AuthUser | null;
}

// Keys for storing impersonation state in localStorage
const IMPERSONATION_KEY = 'impersonation_state';

class AuthService {
  // Detect if we're on a mobile device
  private isMobile(): boolean {
    const userAgent = navigator.userAgent;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isIOSSafari = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;
    
    const isMobile = isMobileUA || isIOSSafari || (isTouchDevice && isSmallScreen);
    console.log('Mobile detection:', { userAgent, isMobileUA, isIOSSafari, isTouchDevice, isSmallScreen, isMobile });
    
    return isMobile;
  }

  // Check for redirect result on app initialization
  async checkRedirectResult(): Promise<AuthUser | null> {
    try {
      console.log('Checking for redirect result...');
      const result = await getRedirectResult(firebaseObject.auth);
      if (result && result.user) {
        console.log('Redirect result found, user authenticated:', result.user.email);
        // Register user with backend
        const authUser = await this.registerUserWithBackend();
        
        return authUser;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting redirect result:', error);
      return null;
    }
  }

  // Sign in with Google
  async signInWithGoogle(): Promise<AuthUser> {
    try {
      // Use redirect on mobile devices, popup on desktop
      const mobile = this.isMobile();
      console.log('Starting Google sign-in, mobile detected:', mobile);
      
      // if (mobile) {
      //   console.log('Using redirect method for mobile');
      //   await signInWithRedirect(auth, googleProvider);
      //   // Return promise that will be resolved when redirect completes
      //   // The actual user data will be available via checkRedirectResult()
      //   throw new Error('REDIRECT_IN_PROGRESS');
      // } else {
        console.log('Using popup method for desktop');
        const result = await signInWithPopup(firebaseObject.auth, firebaseObject.googleProvider);
        const user = result.user;
        console.log('Popup sign-in successful:', user.email);
        
        // Register user with backend
        const authUser = await this.registerUserWithBackend();

        return authUser;
      // }
    } catch (error) {
      // If popup fails (common on iOS), fallback to redirect
      if (error instanceof Error && error.message.includes('popup')) {
        console.log('Popup failed, using redirect method');
        await signInWithRedirect(firebaseObject.auth, firebaseObject.googleProvider);
        throw new Error('REDIRECT_IN_PROGRESS');
      }
      
      if (error instanceof Error && error.message === 'REDIRECT_IN_PROGRESS') {
        console.log('Redirect in progress, throwing REDIRECT_IN_PROGRESS');
        throw error;
      }
      
      console.error('Error signing in with Google:', error);
      throw error;
    }
  }

  // Sign in with Microsoft
  async signInWithMicrosoft(): Promise<AuthUser> {
    try {
      // Use redirect on mobile devices, popup on desktop
      const mobile = this.isMobile();
      console.log('Starting Microsoft sign-in, mobile detected:', mobile);
      
      if (mobile) {
        console.log('Using redirect method for mobile');
        await signInWithRedirect(firebaseObject.auth, firebaseObject.microsoftProvider);
        throw new Error('REDIRECT_IN_PROGRESS');
      } else {
        console.log('Using popup method for desktop');
        const result = await signInWithPopup(firebaseObject.auth, firebaseObject.microsoftProvider);
        const user = result.user;
        console.log('Popup sign-in successful:', user.email);
        
        
        // Register user with backend
       const authUser = await this.registerUserWithBackend();

        return authUser;
      }
    } catch (error) {
      // If popup fails (common on iOS), fallback to redirect
      if (error instanceof Error && error.message.includes('popup')) {
        console.log('Popup failed, using redirect method');
        await signInWithRedirect(firebaseObject.auth, firebaseObject.microsoftProvider);
        throw new Error('REDIRECT_IN_PROGRESS');
      }
      
      if (error instanceof Error && error.message === 'REDIRECT_IN_PROGRESS') {
        console.log('Redirect in progress, throwing REDIRECT_IN_PROGRESS');
        throw error;
      }
      
      console.error('Error signing in with Microsoft:', error);
      throw error;
    }
  }

  // Sign in with email and password
  async signInWithEmailAndPassword(email: string, password: string): Promise<AuthUser> {
    try {
      const result = await signInWithEmailAndPassword(firebaseObject.auth, email, password);
      const user = result.user;
      
      // Register user with backend
      const authUser = await this.registerUserWithBackend();

      return authUser;
    } catch (error) {
      console.error('Error signing in with email and password:', error);
      throw error;
    }
  }

  // Create account with email and password
  async createUserWithEmailAndPassword(email: string, password: string, displayName?: string): Promise<AuthUser> {
    try {
      const result = await createUserWithEmailAndPassword(firebaseObject.auth, email, password);
      const user = result.user;
      
      // Update display name if provided
      if (displayName && user) {
        await updateProfile(user, { displayName });
      }
      
      // Register user with backend
      const authUser = await this.registerUserWithBackend();

      return authUser;
    } catch (error) {
      console.error('Error creating user with email and password:', error);
      throw error;
    }
  }

  // Send password reset email
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(firebaseObject.auth, email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  // Change password (requires reauthentication)
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = firebaseObject.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No authenticated user');
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await signOut(firebaseObject.auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return firebaseObject.auth.currentUser;
  }

  // Listen to authentication state changes
  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(firebaseObject.auth, async (user) => {
      if (user) {
        try {
          // Fetch the complete user data from backend
          const backendUser = await this.getBackendUserData();
          callback(backendUser);
        } catch (error) {
          console.error('Error fetching backend user data:', error);
          // Fallback to Firebase data if backend fails
          callback({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            picture: user.photoURL,
            role: null,
            is_active: undefined // Unknown if backend fails
          });
        }
      } else {
        callback(null);
      }
    });
  }

  // Register user with backend after successful authentication
  async registerUserWithBackend(): Promise<AuthUser> {
    try {
      console.log('Registering user with backend...');
      const response = await apiService.post('/api/user/login');
      console.log('User registered successfully:', response.data);
      return {
        ...response.data.user,
        language: response.data.language,
        currency: response.data.currency,
        number_format: response.data.number_format ?? 'comma',
        vat_rate: response.data.vat_rate ?? null,
        // These are returned at the TOP LEVEL of /login (siblings of `user`),
        // so they must be copied across explicitly — a `...response.data.user`
        // spread does not include them.
        inventory_source_priority: response.data.inventory_source_priority ?? 'dms_first',
        inventory_show_alternative: response.data.inventory_show_alternative ?? true,
        inventory_show_importer_qty: response.data.inventory_show_importer_qty === true,
        inventory_price_priority: response.data.inventory_price_priority ?? 'original_first',
        delivery_default_days: response.data.delivery_default_days ?? 5,
        delivery_zones: response.data.delivery_zones ?? undefined,
        inventory_location_priority: response.data.inventory_location_priority ?? 'list_order',
        warehouses: response.data.warehouses ?? [],
        home_warehouse: response.data.home_warehouse ?? null,
        home_city: response.data.home_city ?? null,
        home_warehouses: response.data.home_warehouses ?? [],
        roi_calculator_enabled: response.data.roi_calculator_enabled ?? false,
        roi_b2b_enabled: response.data.roi_b2b_enabled ?? false,
        roi_b2c_enabled: response.data.roi_b2c_enabled ?? false,
        roi_monthly_quotes: response.data.roi_monthly_quotes ?? null,
        roi_fte_multiplier: response.data.roi_fte_multiplier ?? null,
        roi_currency: response.data.roi_currency ?? null,
        roi_fte_cost: response.data.roi_fte_cost ?? null,
        roi_cost_per_quote: response.data.roi_cost_per_quote ?? null,
        roi_b2c_quotes: response.data.roi_b2c_quotes ?? null,
        roi_b2c_cr: response.data.roi_b2c_cr ?? null,
        roi_b2c_aov: response.data.roi_b2c_aov ?? null,
        roi_b2c_gm: response.data.roi_b2c_gm ?? null,
        roi_b2c_cpq: response.data.roi_b2c_cpq ?? null,
        roi_b2c_pct: response.data.roi_b2c_pct ?? null,
        roi_b2c_pricing_mode: response.data.roi_b2c_pricing_mode ?? null,
        organization_name: response.data.organization_name ?? null,
        home_organization_id: response.data.home_organization_id ?? null,
        home_organization_name: response.data.home_organization_name ?? null,
        is_outside_home_org: response.data.is_outside_home_org ?? false,
      } as AuthUser;
    } catch (error) {
      console.error('Error registering user with backend:', error);
      throw error;
    }
  }

  // Get backend user data for authenticated user
  async getBackendUserData(): Promise<AuthUser> {
    try {
      const response = await apiService.post('/api/user/login');
      return {
        ...response.data.user,
        language: response.data.language,
        currency: response.data.currency,
        number_format: response.data.number_format ?? 'comma',
        vat_rate: response.data.vat_rate ?? null,
        // These are returned at the TOP LEVEL of /login (siblings of `user`),
        // so they must be copied across explicitly — a `...response.data.user`
        // spread does not include them.
        inventory_source_priority: response.data.inventory_source_priority ?? 'dms_first',
        inventory_show_alternative: response.data.inventory_show_alternative ?? true,
        inventory_show_importer_qty: response.data.inventory_show_importer_qty === true,
        inventory_price_priority: response.data.inventory_price_priority ?? 'original_first',
        delivery_default_days: response.data.delivery_default_days ?? 5,
        delivery_zones: response.data.delivery_zones ?? undefined,
        inventory_location_priority: response.data.inventory_location_priority ?? 'list_order',
        warehouses: response.data.warehouses ?? [],
        home_warehouse: response.data.home_warehouse ?? null,
        home_city: response.data.home_city ?? null,
        home_warehouses: response.data.home_warehouses ?? [],
        roi_calculator_enabled: response.data.roi_calculator_enabled ?? false,
        roi_b2b_enabled: response.data.roi_b2b_enabled ?? false,
        roi_b2c_enabled: response.data.roi_b2c_enabled ?? false,
        roi_monthly_quotes: response.data.roi_monthly_quotes ?? null,
        roi_fte_multiplier: response.data.roi_fte_multiplier ?? null,
        roi_currency: response.data.roi_currency ?? null,
        roi_fte_cost: response.data.roi_fte_cost ?? null,
        roi_cost_per_quote: response.data.roi_cost_per_quote ?? null,
        roi_b2c_quotes: response.data.roi_b2c_quotes ?? null,
        roi_b2c_cr: response.data.roi_b2c_cr ?? null,
        roi_b2c_aov: response.data.roi_b2c_aov ?? null,
        roi_b2c_gm: response.data.roi_b2c_gm ?? null,
        roi_b2c_cpq: response.data.roi_b2c_cpq ?? null,
        roi_b2c_pct: response.data.roi_b2c_pct ?? null,
        roi_b2c_pricing_mode: response.data.roi_b2c_pricing_mode ?? null,
        organization_name: response.data.organization_name ?? null,
        home_organization_id: response.data.home_organization_id ?? null,
        home_organization_name: response.data.home_organization_name ?? null,
        is_outside_home_org: response.data.is_outside_home_org ?? false,
      } as AuthUser;
    } catch (error) {
      console.error('Error fetching backend user data:', error);
      throw error;
    }
  }

  // Refresh current user data from backend (useful when user data might have changed)
  async refreshCurrentUser(): Promise<AuthUser | null> {
    const firebaseUser = this.getCurrentUser();
    if (!firebaseUser) {
      return null;
    }

    try {
      return await this.getBackendUserData();
    } catch (error) {
      console.error('Error refreshing user data:', error);
      return null;
    }
  }

  // Start impersonating a user (admin only)
  async startImpersonation(userUid: string): Promise<ImpersonationState> {
    try {
      const response = await apiService.post(`/api/admin/users/${userUid}/impersonate`);
      const { impersonated_user, admin_user } = response.data;
      
      const impersonationState: ImpersonationState = {
        isImpersonating: true,
        impersonatedUser: {
          uid: impersonated_user.uid,
          email: impersonated_user.email,
          displayName: impersonated_user.name,
          picture: impersonated_user.picture,
          role: impersonated_user.role,
          id: impersonated_user.id,
          name: impersonated_user.name,
          organization_id: impersonated_user.organization_id ?? null,
        },
        originalAdmin: {
          uid: admin_user.uid,
          email: admin_user.email,
          displayName: admin_user.name,
          picture: admin_user.picture,
          role: admin_user.role,
          id: admin_user.id,
          name: admin_user.name,
        }
      };
      
      // Store impersonation state in localStorage BEFORE fetching the profile,
      // so ApiService attaches the X-Impersonate-UID header to the call below.
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonationState));

      // Fetch the impersonated user's full backend profile (organization_id,
      // language, currency, ROI settings) so the impersonated view matches
      // exactly what that user sees in their own session.
      try {
        const fullProfile = await this.getBackendUserData();
        impersonationState.impersonatedUser = {
          ...impersonationState.impersonatedUser,
          ...fullProfile,
        };
        localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonationState));
      } catch (profileError) {
        console.warn('Could not load full profile for impersonated user, using basic data:', profileError);
      }

      return impersonationState;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  }

  // Stop impersonating and return to admin user
  stopImpersonation(): void {
    localStorage.removeItem(IMPERSONATION_KEY);
  }

  // Get current impersonation state
  getImpersonationState(): ImpersonationState | null {
    try {
      const stored = localStorage.getItem(IMPERSONATION_KEY);
      if (stored) {
        return JSON.parse(stored) as ImpersonationState;
      }
      return null;
    } catch (error) {
      console.error('Error reading impersonation state:', error);
      return null;
    }
  }

  // Check if currently impersonating
  isImpersonating(): boolean {
    const state = this.getImpersonationState();
    return state?.isImpersonating ?? false;
  }
}

  export default new AuthService();
